// src/pages/JoinOurTeam.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./JoinOurTeam.css";

const cityChoices = [
  "Toronto","New York","Boston","Chicago","New Orleans","Austin","Dallas","Houston","Miami"
];

export default function JoinOurTeam() {
  const formRef = useRef(null);

  // Turnstile refs
  const widgetIdRef = useRef(null);
  const tsTokenRef  = useRef("");

  const [notice, setNotice] = useState(null);
  const [headshotPreview, setHeadshotPreview] = useState(null);
  const [dishPreviews, setDishPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // ---- helpers ---------------------------------------------------------

  // Render (or re-render) the invisible widget when library is ready
  const renderWidget = useCallback(() => {
    const el = document.getElementById("cf-turnstile");
    if (!el || !window.turnstile) return;

    // if already rendered, don't render twice
    if (widgetIdRef.current) return;

    const sitekey =
      (typeof window !== "undefined" && window.TURNSTILE_SITE_KEY) ||
      "0x4AAAAAAB7MQ0uzOgJiN9fM"; // your real site key as fallback

    widgetIdRef.current = window.turnstile.render("#cf-turnstile", {
      sitekey,
      size: "invisible",               // <— invisible mode
      appearance: "execute",           // prefer auto-execution style
      theme: "light",

      callback: (token) => {
        tsTokenRef.current = token || "";
      },
      "expired-callback": () => {
        tsTokenRef.current = "";
      },
      "error-callback": () => {
        tsTokenRef.current = "";
      },
      "timeout-callback": () => {
        tsTokenRef.current = "";
      },
    });
  }, []);

  // Try to render when script arrives or after a short poll
  useEffect(() => {
    let tries = 0;
    const tick = setInterval(() => {
      tries++;
      if (window.turnstile && document.getElementById("cf-turnstile")) {
        renderWidget();
        clearInterval(tick);
      } else if (tries > 50) {
        clearInterval(tick);
        console.warn("Turnstile did not initialize. Check index.html script + CSP.");
      }
    }, 100);
    return () => clearInterval(tick);
  }, [renderWidget]);

  // Safely reset widget + token
  const resetTurnstile = () => {
    try {
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.reset(widgetIdRef.current);
      }
    } catch {}
    tsTokenRef.current = "";
  };

  // Ensure we have a fresh token; if not, execute invisible challenge and wait
  const ensureToken = async () => {
    if (tsTokenRef.current) return tsTokenRef.current;
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.execute(widgetIdRef.current);
    }
    // wait up to ~8s for callback to populate token
    for (let i = 0; i < 80; i++) {
      await new Promise((r) => setTimeout(r, 100));
      if (tsTokenRef.current) return tsTokenRef.current;
    }
    throw new Error("Please complete the security check.");
  };

  // ---- local previews --------------------------------------------------
  const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB target after compression
  const MAX_LONG_EDGE = 1600; // pixels

  const onHeadshotChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setHeadshotPreview(null);
      return;
    }
    if (!f.type.startsWith("image/")) {
      setNotice({ type: "err", msg: "Headshot must be an image file." });
      e.target.value = "";
      return;
    }
    if (f.size > MAX_IMAGE_SIZE) {
      setNotice({ type: "err", msg: "Headshot must be under 2 MB." });
      e.target.value = "";
      return;
    }
    setHeadshotPreview(f ? URL.createObjectURL(f) : null);
  };

  const onDishesChange = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    const valid = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) continue;
      if (f.size > MAX_IMAGE_SIZE) continue;
      valid.push(f);
    }
      setDishPreviews(valid.map((f) => ({ url: URL.createObjectURL(f), name: f.name })));
      if (valid.length < files.length) {
        setNotice({ type: "err", msg: "Some dish files were rejected (max 5 files, images under 2MB)." });
    }
  };

  // ---- submit ----------------------------------------------------------
  const onSubmit = async (e) => {
    e.preventDefault();
    setNotice(null);

    try {
      const token = await ensureToken(); // invisible challenge run if needed

      // Build FormData manually (we will resize images client-side)
      const fd = new FormData();

      // append text fields
      const formEl = formRef.current;
      const inputs = formEl.querySelectorAll('input[name]:not([type=file]), textarea[name], select[name]');
      inputs.forEach((el) => {
        if (!el.name) return;
        fd.set(el.name, el.value || "");
      });

      fd.set("turnstileToken", token);

      // compress + resize helper: try WebP first then JPEG, stepping down quality until <= MAX_IMAGE_SIZE
      const compressAndResize = (file) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = async () => {
            try {
              const w = img.width;
              const h = img.height;
              const long = Math.max(w, h);
              let nw = w;
              let nh = h;
              if (long > MAX_LONG_EDGE) {
                const scale = MAX_LONG_EDGE / long;
                nw = Math.round(w * scale);
                nh = Math.round(h * scale);
              }
              const c = document.createElement('canvas');
              c.width = nw;
              c.height = nh;
              const ctx = c.getContext('2d');
              ctx.drawImage(img, 0, 0, nw, nh);

              const qualities = [0.9, 0.8, 0.7, 0.6, 0.55, 0.5];
              const toBlob = (type, q) => new Promise((res) => c.toBlob(res, type, q));

              // Try WebP
              for (const q of qualities) {
                const webpBlob = await toBlob('image/webp', q);
                if (webpBlob && webpBlob.size <= MAX_IMAGE_SIZE && webpBlob.type === 'image/webp') {
                  const outFile = new File([webpBlob], file.name.replace(/\.[^/.]+$/, '') + '.webp', { type: 'image/webp' });
                  return resolve(outFile);
                }
              }

              // Fallback to JPEG
              for (const q of qualities) {
                const jpegBlob = await toBlob('image/jpeg', q);
                if (jpegBlob && jpegBlob.size <= MAX_IMAGE_SIZE) {
                  const outFile = new File([jpegBlob], file.name.replace(/\.[^/.]+$/, '') + '.jpg', { type: 'image/jpeg' });
                  return resolve(outFile);
                }
              }

              // Return last attempt (lowest quality) even if >limit
              const last = await toBlob('image/jpeg', qualities[qualities.length - 1]);
              if (last) {
                const outFile = new File([last], file.name.replace(/\.[^/.]+$/, '') + '.jpg', { type: last.type || 'image/jpeg' });
                return resolve(outFile);
              }
              return reject(new Error('Unable to compress image'));
            } catch (err) {
              return reject(err);
            }
          };
          img.onerror = (err) => reject(err || new Error('Image load error'));
          img.src = URL.createObjectURL(file);
        });
      };

      // handle files
      setUploading(true);
      setUploadProgress(5);

      const headshotInput = formEl.querySelector('input[name="photo"]');
      if (headshotInput && headshotInput.files && headshotInput.files[0]) {
        const hf = headshotInput.files[0];
        if (hf.type.startsWith('image/')) {
          const processed = await compressAndResize(hf);
          if (processed.size > MAX_IMAGE_SIZE) {
            throw new Error('Headshot could not be compressed under 2 MB. Try a smaller image.');
          }
          fd.append('photo', processed, processed.name);
        }
      }

      const dishInput = formEl.querySelector('input[name="dishes"]');
      if (dishInput && dishInput.files && dishInput.files.length) {
        const files = Array.from(dishInput.files).slice(0, 5);
        let idx = 0;
        for (const f of files) {
          if (!f.type.startsWith('image/') || f.size > MAX_IMAGE_SIZE) {
            idx++;
            continue;
          }
          try {
            const processedDish = await compressAndResize(f);
            if (processedDish.size > MAX_IMAGE_SIZE) {
              throw new Error('One of the dish photos could not be compressed under 2 MB.');
            }
            fd.append('dishes', processedDish, processedDish.name);
          } catch (err) {
            throw err; // bubble up so user can retry with smaller images
          }
          idx++;
          setUploadProgress(5 + Math.round((idx / files.length) * 40));
        }
      }

      const res = await fetch("/api/join", {
        method: "POST",
        body: fd,
        headers: { "cf-turnstile-response": token },
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Submit failed (${res.status})`);
      }

      setNotice({ type: "ok", msg: "Thanks! We’ve received your application." });
      formRef.current?.reset?.();
      setHeadshotPreview(null);
      setDishPreviews([]);
      resetTurnstile(); // clear token for next submission
      setUploadProgress(100);
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 800);
    } catch (err) {
      setNotice({ type: "err", msg: err?.message || "Something went wrong." });
      resetTurnstile();
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ---- render ----------------------------------------------------------
  return (
    <main className="join-wrap">
      <h1 className="join-title">Join Our Team</h1>
      <p className="join-lead">Tell us about you and your cuisine. A real person will review and reply.</p>

      {notice && <div className={`alert ${notice.type}`}>{notice.msg}</div>}

      <form ref={formRef} className="join-form" encType="multipart/form-data" onSubmit={onSubmit}>
        {/* Basics */}
        <div className="grid two">
          <label>
            <span>Name</span>
            <input type="text" name="name" placeholder="Your full name" required />
          </label>
          <label>
            <span>City</span>
            <select name="city" required defaultValue="">
              <option value="" disabled>Select your city</option>
              {cityChoices.map((c) => (
                <option key={c} value={c.toLowerCase()}>{c}</option>
              ))}
            </select>
          </label>

          <label>
            <span>Email</span>
            <input type="email" name="email" placeholder="you@example.com" required />
          </label>
          <label>
            <span>Phone</span>
            <input type="tel" name="phone" placeholder="(555) 555-5555" />
          </label>
        </div>

        <div className="grid two">
          <label>
            <span>Specialties</span>
            <input
              type="text"
              name="specialties"
              placeholder="e.g. Italian, Farm-to-Table, Vegan"
              list="specialty-suggestions"
            />
            <datalist id="specialty-suggestions">
              <option>Farm-to-Table</option><option>Seasonal</option><option>Vegan</option>
              <option>BBQ</option><option>Pastry</option><option>Seafood</option>
            </datalist>
          </label>

          <label>
            <span>Website (optional)</span>
            <input type="url" name="website" placeholder="https://your-site.com" />
          </label>
        </div>

        <label>
          <span>Short Bio</span>
          <textarea name="bio" placeholder="A few lines about your background and cuisine…"></textarea>
        </label>

        <div className="grid two">
          <label><span>Instagram</span><input type="url" name="instagram" placeholder="https://instagram.com/yourhandle" /></label>
          <label><span>TikTok</span><input type="url" name="tiktok" placeholder="https://tiktok.com/@yourhandle" /></label>
          <label><span>YouTube</span><input type="url" name="youtube" placeholder="https://youtube.com/@yourchannel" /></label>
          <label><span>Facebook</span><input type="url" name="facebook" placeholder="https://facebook.com/yourpage" /></label>
          <label><span>LinkedIn</span><input type="url" name="linkedin" placeholder="https://linkedin.com/in/yourname" /></label>
          <label><span>Yelp</span><input type="url" name="yelp" placeholder="https://yelp.com/biz/your-biz" /></label>
          <label className="col-span-2"><span>OpenTable</span><input type="url" name="opentable" placeholder="https://opentable.com/r/your-restaurant" /></label>
        </div>

        <div className="uploads">
          <label>
            <span>Headshot</span>
            <input type="file" name="photo" accept="image/*" onChange={onHeadshotChange} />
            {headshotPreview && <div className="preview"><img src={headshotPreview} alt="Headshot preview" /></div>}
          </label>

          <label>
            <span>Dish Photos (up to 5)</span>
            <input type="file" name="dishes" accept="image/*" multiple onChange={onDishesChange} />
            {!!dishPreviews.length && (
              <div className="preview">
                {dishPreviews.map((p, i) => <img key={i} src={p.url} alt={p.name} />)}
              </div>
            )}
          </label>
        </div>

        {/* Invisible Turnstile mount point (hidden) */}
        <div id="cf-turnstile" style={{ display: "none" }} aria-hidden="true" />

        <div className="actions">
          <button className="btn gold" type="submit">Submit Application</button>
        </div>
      </form>
    </main>
  );
}